import { STORAGE_TIME_MAP } from 'src/constants'
import { ListingVariant } from 'src/lib/listingVariants/types'
import { IConfiguration, ILocation, StorageTime } from 'src/types/graphql'
import { ClickIds } from 'src/utils/utms'

export type BuildLeadFormWaMessageArgs = {
  /**
   * Optional — kept for symmetry with the call sites that already pass the
   * surrounding variant slice. The helper currently doesn't read it; new
   * callers (SpecialistCard) can omit it.
   */
  variant?: Pick<ListingVariant, 'meta' | 'leadForm'>
  location: ILocation | null
  configuration: IConfiguration | null
  values: {
    name?: string
    sqm?: number | null
    quantity?: number | null
    when?: string | null
    additionalInfo?: string
  }
  /** Locale-aware: 'es' (default) or 'en'. */
  locale?: 'es' | 'en'
  /** Site origin for the listing URL. Default: 'https://www.spotme.mx'. */
  origin?: string
  /**
   * Short hashed PostHog session token (typically 4 base32 chars from
   * `hashSessionId(posthog.sessionId())`). When present, appended to the
   * listing URL as `?sid={token}` (or `&sid=` if the URL already has
   * non-tracking query params). The token rides on the URL only — we
   * used to also duplicate it on the `Ref:` line via a pipe separator
   * (`Ref: abc123 | 4f7m`), but the customer sees the prefill draft
   * before sending and that suffix reads as a tracking marker. The URL
   * `?sid=` carrier is cleaner — customers expect tracking params on
   * URLs and don't second-guess them.
   * Skipped silently when empty — graceful degradation falls back to
   * phone-based attribution server-side.
   */
  sessionToken?: string
  /**
   * Ad-platform click IDs (gclid / fbclid / msclkid) from the visitor's
   * attribution cookies. Each present id is appended to the prefill listing
   * URL as a query param so the LangGraph `complete` node can fire the
   * server-side `qualified_whatsapp_lead` conversion against the originating
   * platform — the backend extractor reads them straight off the URL. Omitted
   * ids are skipped; organic visitors (no click id) change nothing. Pass via
   * `getClickIdsFromCookies()` from a click handler, or `useClickIdsForPrefill()`
   * from a render-time (SSR'd href) call site to stay hydration-safe.
   */
  clickIds?: ClickIds
  /**
   * Optional greeting override. Defaults to a locale-aware
   * "Hola, me interesa este espacio:" / "Hi, I'm interested in this
   * space:". The listing-page floating WA button passes a variant-
   * specific greeting (e.g. "Hola, me interesa rentar este espacio
   * comercial para mi negocio:" for broker, "...este espacio de mini
   * bodega tradicional:" for traditional) so the LangGraph analyzer
   * gets intent context up front. Pass an empty string to skip the
   * greeting line entirely.
   */
  greeting?: string
}

/**
 * Tracking params we strip from `location.urlRoute` before appending
 * `?sid={token}`. These accumulate on the URL when the user landed via
 * an ad — `utm_*` and `hsa_*` are added by HubSpot Ads or manually;
 * `gclid` / `fbclid` / `msclkid` are auto-tagged by the ad platform.
 *
 * Why strip `utm_*` and `hsa_*`: they're noise in the WA prefill (the
 * chatbot doesn't act on them) AND the attribution pipeline already
 * captured them server-side via cookies + `classifyAttribution` at
 * pageview time. Keeping them on the WA URL just makes the message
 * look tracker-heavy and bloats the line, pushing the useful `Ref:`
 * line below the visible draft preview on mobile WhatsApp.
 *
 * Why we DO NOT strip `gclid` / `fbclid` / `msclkid` (changed 2026-05-26):
 * the LangGraph `complete` node fires server-side Google Ads / Meta CAPI /
 * Microsoft Ads enhanced-conversion uploads (`QualifiedWhatsappLead*`)
 * keyed on the inbound conversation's click ID. The backend extractor at
 * `spotme-server/src/LangGraphAgent/utils/attributionUtils.ts` already
 * parses these query params from the first SpotMe URL in the user's
 * first message — but only when the URL still carries them. Previously
 * the strip killed Google's loop for every WA inbound. Now we accept the
 * 50-200 extra chars on the URL for paid-traffic users only (organic
 * traffic users carry no click ID, so they see no change) in exchange
 * for closing the conversion loop for the highest-value cohort.
 *
 * If a future cookie+session-store pipeline can carry click IDs server-
 * side reliably enough to match the message-extraction path's coverage,
 * we can revisit and strip again. Today the prefill is the most reliable
 * carrier.
 */
const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'hsa_acc',
  'hsa_ad',
  'hsa_cam',
  'hsa_grp',
  'hsa_kw',
  'hsa_mt',
  'hsa_net',
  'hsa_src',
  'hsa_tgt',
  'hsa_ver'
]

function stripTrackingParams(urlRoute: string): string {
  const queryStart = urlRoute.indexOf('?')
  if (queryStart === -1) return urlRoute
  const path = urlRoute.slice(0, queryStart)
  const queryString = urlRoute.slice(queryStart + 1)
  if (queryString.length === 0) return path
  const kept = queryString.split('&').filter((pair) => {
    const eq = pair.indexOf('=')
    const key = eq === -1 ? pair : pair.slice(0, eq)
    return key.length > 0 && !TRACKING_PARAMS.includes(key)
  })
  return kept.length === 0 ? path : `${path}?${kept.join('&')}`
}

/**
 * Render `values.when` (a StorageTime enum value) into a human-readable
 * label. STORAGE_TIME_MAP is Spanish-only today; for an English message
 * we still emit Spanish because the underlying enum has no English copy
 * — better the user sees "Unos meses (3-5 meses)" than the raw
 * enum key `FewMonthsSix`.
 */
function formatStorageTime(raw: string): string {
  if (raw in STORAGE_TIME_MAP) {
    return STORAGE_TIME_MAP[raw as StorageTime]
  }
  return raw
}

const COPY = {
  es: {
    intro: 'Hola, me interesa este espacio:',
    name: 'Nombre',
    sqm: 'Tamaño (m²)',
    quantity: 'Cantidad',
    when: 'Cuándo lo necesito',
    info: 'Comentarios'
  },
  en: {
    intro: "Hi, I'm interested in this space:",
    name: 'Name',
    sqm: 'Size (m²)',
    quantity: 'Quantity',
    when: 'When I need it',
    info: 'Notes'
  }
} as const

/**
 * Produce the WhatsApp-prefill message body for a lead form submission.
 * The caller passes this to `buildWhatsappUrl(message, recipient)`.
 *
 * Structure (single `\n` between blocks — no blank lines):
 *   1. Greeting/intro line
 *   2. Listing URL line (when location.urlRoute is present), with
 *      tracking params stripped and `?sid={token}` appended when
 *      sessionToken is provided — the URL is the SOLE carrier of the
 *      session token
 *   3. `Ref: {nanoId}` line (when configuration.nanoId is present)
 *   4. Collected qualifiers (name, sqm, quantity, when, info), one per line
 *
 * Why no blank lines: iOS WhatsApp's `wa.me` prefill flow drops the message
 * into a draft text input that collapses consecutive `%0A%0A` to a single
 * space on focus on some versions — the customer then sends the flattened
 * version. A single `\n` survives more reliably than two. We trade the
 * visual breathing room for delivery robustness.
 */
export function buildLeadFormWaMessage(
  args: BuildLeadFormWaMessageArgs
): string {
  const {
    location,
    configuration,
    values,
    locale = 'es',
    origin = 'https://www.spotme.mx',
    sessionToken,
    clickIds,
    greeting
  } = args
  const copy = COPY[locale]
  const token = sessionToken?.trim() || ''

  const lines: string[] = []
  // Greeting line — honour an explicit override (including empty string
  // to suppress it entirely). Undefined falls back to the locale default.
  const intro = greeting !== undefined ? greeting : copy.intro
  if (intro.length > 0) lines.push(intro)

  // Listing URL — use location.urlRoute if present (existing convention).
  // Strip tracking params first (they're noise in WhatsApp and the
  // attribution pipeline already captured them server-side via cookies),
  // then append `sid` so the token survives the common case where the
  // user only keeps the URL line in their reply.
  if (location?.urlRoute) {
    const cleanRoute = stripTrackingParams(location.urlRoute)
    // Collect query params for the prefill URL: the session token (`sid`)
    // plus any ad-platform click IDs. The session token rides the URL only
    // (clean of customer-visible markers); the click IDs let the backend
    // close the `qualified_whatsapp_lead` conversion loop per platform.
    const urlParams: string[] = []
    if (token) urlParams.push(`sid=${token}`)
    if (clickIds?.gclid)
      urlParams.push(`gclid=${encodeURIComponent(clickIds.gclid)}`)
    if (clickIds?.fbclid)
      urlParams.push(`fbclid=${encodeURIComponent(clickIds.fbclid)}`)
    if (clickIds?.msclkid)
      urlParams.push(`msclkid=${encodeURIComponent(clickIds.msclkid)}`)
    const query = urlParams.length
      ? `${cleanRoute.includes('?') ? '&' : '?'}${urlParams.join('&')}`
      : ''
    lines.push(`${origin}${cleanRoute}${query}`)
  }

  // Configuration reference: short ID if available. The session token
  // is NOT duplicated here — it already rides on the URL via `?sid=`
  // above, and a customer-visible `| 4f7m` suffix on the Ref line reads
  // as a tracking marker (bad UX, no upside since the URL is the
  // canonical carrier the backend extracts from).
  if (configuration?.nanoId) {
    lines.push(`Ref: ${configuration.nanoId}`)
  }

  // Form-collected qualifiers.
  const fieldLines: string[] = []
  if (values.name && values.name.trim().length > 0) {
    fieldLines.push(`${copy.name}: ${values.name.trim()}`)
  }
  if (values.sqm != null && values.sqm > 0) {
    fieldLines.push(`${copy.sqm}: ${values.sqm}`)
  }
  // Only surface quantity when it's a meaningful signal (>1). Traditional
  // and industrial-warehouse listings are inherently single-unit asks, and
  // `quantity: 1` reads as noise — the specialist already infers it.
  if (values.quantity != null && values.quantity > 1) {
    fieldLines.push(`${copy.quantity}: ${values.quantity}`)
  }
  if (values.when) {
    fieldLines.push(`${copy.when}: ${formatStorageTime(values.when)}`)
  }
  if (values.additionalInfo && values.additionalInfo.trim().length > 0) {
    fieldLines.push(`${copy.info}: ${values.additionalInfo.trim()}`)
  }
  if (fieldLines.length > 0) {
    lines.push(...fieldLines)
  }

  return lines.join('\n')
}
